import axios, { AxiosResponse, AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { authStore } from '../stores';
import { PROJECT_PART_HEADER, PROJECT_PART_HEADER_VALUES, PROJECT_CLIENT_VERSION_HEADER } from '@sweatpals/shared';
import jwtDecode from 'jwt-decode';
import { API_REQUEST_TIMEOUT_MS, API_URL } from '../constants';

export const isExpirationTimePassed = (exp: number) => new Date().getTime() - 2000 >= exp * 1000;

const httpClientFactory = (): AxiosInstance => {
	const httpClient = axios.create({
		baseURL: API_URL,
		timeout: API_REQUEST_TIMEOUT_MS || 60000,
		headers: {
			[PROJECT_PART_HEADER]: PROJECT_PART_HEADER_VALUES.LANDING,
			[PROJECT_CLIENT_VERSION_HEADER]: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
		},
	});

	axiosRetry(httpClient, {
		retries: 3,
		retryDelay: () => 3000,
		retryCondition: error => error?.response?.status === 502,
	});

	const authHeaderInterceptor = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
		if (typeof window !== 'undefined') {
			const { access: token } = authStore.readStorage() || {};

			if (token && !config.headers.Authorization) {
				config.headers['Authorization'] = `${token}`;
			}
		}

		return config;
	};

	const responseHandlerInterceptor = (response: AxiosResponse): AxiosResponse => response;
	const errorsHandlerInterceptor = async (error: AxiosError): Promise<any> => {
		const originalRequest = error.config;

		const response = error.response;

		if (typeof window !== 'undefined' && response?.status === 401) {
			const { refresh: refreshToken } = authStore.readStorage() || {};

			const { exp: refreshTokenExpirationTime, id: userId } = refreshToken
				? jwtDecode<{ exp: number; id: string }>(refreshToken)
				: { exp: undefined, id: undefined };

			if (refreshToken && !isExpirationTimePassed(refreshTokenExpirationTime)) {
				try {
					if (!userId) {
						throw new Error('Userid not found');
					}

					// pure axios client to avoid infinite interceptor loop
					const response = await axios.post(`${httpClient.defaults.baseURL}/auth/refresh`, { refreshToken, userId });

					if (response.status === 201 && response.data?.access_token && response.data?.refresh_token) {
						authStore.setAuth(response.data.access_token, response.data.refresh_token);

						originalRequest.headers['Authorization'] = `${response.data.access_token}`;

						return await httpClient(originalRequest);
					}
				} catch (error) {
					authStore.logout();
				}
			}
		}

		return Promise.reject(error);
	};

	httpClient.interceptors.request.use(authHeaderInterceptor, async err => Promise.reject(err));
	httpClient.interceptors.response.use(responseHandlerInterceptor, errorsHandlerInterceptor);

	return httpClient;
};

export default httpClientFactory;
